
import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { ScrollScope } from "../../core/managers/ScrollScopeRegistry";
import { StringObject } from "../../objects/StringObject";

/**
 * Interface representing the internal state of a scroll container.
 * Tracks current position, target position, and animation parameters.
 */
interface ScrollContainerState {
  current: number;
  target: number;
  maxScroll: number;
  velocity: number;
  lerp: number;
  isDragging: boolean;
  isActive: boolean;
  scope: ScrollScope;
}

/**
 * Module that provides smooth scrolling behavior for isolated containers.
 * Allows nested elements to have independent smooth scroll physics similar to the main window.
 * 
 * Usage:
 * Add `string="scroll-container"` to any block element.
 * Optional: `string-lerp="0.1"` to controls the smoothing factor.
 */
export class StringScrollContainer extends StringModule {
  /**
   * WeakMap storing the state for each managed HTMLElement.
   */
  private states = new WeakMap<HTMLElement, ScrollContainerState>();
  private wheelHandlers = new WeakMap<HTMLElement, (event: WheelEvent) => void>();
  private scrollHandlers = new WeakMap<HTMLElement, (event: Event) => void>();

  /**
   * Initializes the StringScrollContainer module.
   * Registers the `lerp` attribute configuration.
   * 
   * @param context The shared StringTune context.
   */
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "scroll-container";
    this.attributesToMap.push({ key: "lerp", type: "number", fallback: 0.1 });
  }

  /**
   * Called when a new object with `string="scroll-container"` is initialized.
   * Sets up initial state, event listeners, and default styles.
   * 
   * @param object The StringObject instance being connected.
   */
  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    const el = object.htmlElement;

    if (getComputedStyle(el).overflowY === 'visible') {
      el.style.overflowY = 'auto';
    }

    const state: ScrollContainerState = {
      current: el.scrollTop,
      target: el.scrollTop,
      maxScroll: el.scrollHeight - el.clientHeight,
      velocity: 0,
      lerp: object.getProperty<number>("lerp") || 0.1,
      isDragging: false,
      isActive: false,
      scope: this.scrollScopes.register(el),
    };
    const onWheel = (event: WheelEvent) => this.handleWheel(event, el, state);
    const onScroll = (event: Event) => this.onNativeScroll(event, el, state);

    this.states.set(el, state);
    this.wheelHandlers.set(el, onWheel);
    this.scrollHandlers.set(el, onScroll);

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", onScroll, { passive: true });

    this.measure(el, state);
  }

  /**
   * Called when an object is removed from the DOM or disconnected.
   * Cleans up the state associated with the element.
   * 
   * @param object The StringObject instance being disconnected.
   */
  override onObjectDisconnected(object: StringObject): void {
    const el = object.htmlElement;
    const onWheel = this.wheelHandlers.get(el);
    const onScroll = this.scrollHandlers.get(el);
    if (onWheel) el.removeEventListener("wheel", onWheel);
    if (onScroll) el.removeEventListener("scroll", onScroll);
    this.wheelHandlers.delete(el);
    this.scrollHandlers.delete(el);
    this.states.delete(el);
    this.scrollScopes.unregister(el);
  }

  /**
   * Main animation loop for the module.
   * Updates the scroll position of active containers based on their target state.
   * 
   * @param data Global frame data including time deltas.
   */
  private get effectiveMode(): string {
    const isMobile = window.innerWidth < 1024;
    return isMobile ? this.data.scroll.modeMobile : this.data.scroll.modeDesktop;
  }

  override onFrame(data: StringData): void {
    for (const object of this.objectsOnPage) {
      const el = object.htmlElement;
      const state = this.states.get(el);
      if (!state) continue;

      if (this.effectiveMode === "smooth" && state.isActive && !state.isDragging) {
        this.updateScroll(el, state);
      }
    }
  }

  /**
   * Called when the window or layout is resized.
   * Recalculates scroll boundaries for all managed containers.
   */
  override onResize(): void {
    for (const object of this.objectsOnPage) {
      const el = object.htmlElement;
      const state = this.states.get(el);
      if (state) {
        this.measure(el, state);
        if (this.effectiveMode !== "smooth") {
          state.isActive = false;
          state.current = el.scrollTop;
          state.target = el.scrollTop;
        }
      }
    }
  }

  override onScrollConfigChange(): void {
    for (const object of this.objectsOnPage) {
      const el = object.htmlElement;
      const state = this.states.get(el);
      if (state) {
        state.isActive = false;
        state.current = el.scrollTop;
        state.target = el.scrollTop;
      }
    }
  }

  /**
   * Recalculates the maximum scrollable distance for a container.
   * 
   * @param el The scroll container element.
   * @param state The state object associated with the element.
   */
  private measure(el: HTMLElement, state: ScrollContainerState) {
    state.maxScroll = el.scrollHeight - el.clientHeight;
  }

  /**
   * Handles the mouse wheel event to apply custom scroll physics.
   * Intercepts the event to prevent native scrolling and updates the target position.
   * Allows event propagation if scrolling past the boundaries (scroll chaining).
   * 
   * @param e The WheelEvent triggered by the user.
   * @param el The target scroll container element.
   * @param state The state object associated with the element.
   */
  private handleWheel(e: WheelEvent, el: HTMLElement, state: ScrollContainerState) {
    if (this.effectiveMode !== "smooth") {
      state.isActive = false;
      return;
    }

    const delta = e.deltaY;

    const isScrollingUp = delta < 0;
    const isScrollingDown = delta > 0;

    const atTop = state.target <= 0.1;
    const atBottom = state.target >= state.maxScroll - 0.1;

    if ((isScrollingUp && atTop) || (isScrollingDown && atBottom)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    state.target += delta;

    state.target = Math.max(0, Math.min(state.target, state.maxScroll));

    state.isActive = true;
    state.isDragging = false;
  }

  /**
   * Placeholder for global wheel handler if needed by generic StringModule interface.
   * Specific handling is done via local event listeners in `handleWheel`.
   * 
   * @param e Global WheelEvent.
   */
  override onWheel(e: WheelEvent): void {
  }

  /**
   * Handles native scroll events (dragbars, keyboard, touch).
   * Syncs the internal state with the native scroll position if the custom animation is not active.
   * 
   * @param e The Scroll event.
   * @param el The target scroll container element.
   * @param state The state object associated with the element.
   */
  private onNativeScroll(e: Event, el: HTMLElement, state: ScrollContainerState) {
    if (this.effectiveMode !== "smooth" || !state.isActive) {
      state.current = el.scrollTop;
      state.target = el.scrollTop;
    }
    this.scrollScopes.bump(state.scope);
  }

  /**
   * Updates the element's scrollTop property by interpolating towards the target.
   * Stops the animation if the difference is negligible.
   * 
   * @param el The scroll container element.
   * @param state The state object associated with the element.
   */
  private updateScroll(el: HTMLElement, state: ScrollContainerState) {
    const diff = state.target - state.current;

    if (Math.abs(diff) < 0.1) {
      state.current = state.target;
      state.isActive = false;
    } else {
      state.current += diff * state.lerp;
    }

    el.scrollTop = state.current;
    this.scrollScopes.bump(state.scope);
  }
}
